import type { MenuProduct } from "@/types/menu-product";
import type {
  RestGuestOption,
  RestOptionGroup,
  VisibleBlock,
} from "./types";

/**
 * Товар → блоки опций, пригодные к показу в этой точке.
 *
 * Асимметрия словарей: ГРУППЫ приезжают меню целиком, ОПЦИИ сужены точкой и
 * типом заказа. Поэтому привязка законно ссылается на опцию, которой в
 * выдаче нет, — такую выбрасываем молча, как гостевое меню уже поступает с
 * непродающимися товарами и пустыми категориями.
 */
export function resolveProductOptions(
  product: Pick<MenuProduct, "optionGroupBinds">,
  groups: readonly RestOptionGroup[],
  options: readonly RestGuestOption[],
): VisibleBlock[] {
  if (product.optionGroupBinds.length === 0) return [];

  const groupById = new Map(groups.map((g) => [g.id, g]));
  const optionById = new Map(options.map((o) => [o.id, o]));
  const visible: VisibleBlock[] = [];

  for (const groupId of product.optionGroupBinds) {
    const group = groupById.get(groupId);
    // Выключенная группа не показывается и лимитов не навязывает — так
    // решил домен, иначе товар с выключенной обязательной группой стал бы
    // незаказуемым.
    if (!group || !group.isEnabled) continue;

    for (const block of group.blocks) {
      const binds = block.optionBinds.flatMap((bind) => {
        const option = optionById.get(bind.optionId);
        if (!option) return [];
        return [{
          optionId: option.id,
          name: option.name,
          price: option.price,
          min: bind.min ?? null,
          max: bind.max ?? null,
        }];
      });

      if (binds.length === 0) continue;

      visible.push({
        blockId: block.id,
        blockName: block.name,
        groupId: group.id,
        groupName: group.name,
        min: block.min ?? null,
        max: block.max ?? null,
        binds,
      });
    }
  }

  return visible;
}

/** Есть ли блок, который нельзя оставить пустым. */
export function hasRequiredBlock(blocks: readonly VisibleBlock[]): boolean {
  return blocks.some((block) => (block.min ?? 0) >= 1);
}
